import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ChevronLeft, Loader2, AlertCircle, Settings, Package, ArrowLeft } from 'lucide-react';
import { pluginsAPI, paymentsAPI, kycAPI, creditsAPI } from '../services/api';
import { getPluginConfig, isPluginRegistered } from '../components/plugins';

// ── Mobile detection ──────────────────────────────────────────────────────────
function useIsMobile(bp = 768) {
  const [is, setIs] = useState(window.innerWidth < bp);
  useEffect(() => {
    const h = () => setIs(window.innerWidth < bp);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, [bp]);
  return is;
}

const PLUGIN_ICONS = {
  bank_transfer_global: '🌍',
  bank_transfer_per_user: '👤',
  bank_transfer_per_country: '🗺️',
  crypto_payments: '💰',
  payment_links: '🔗',
  kyc_verification: '🛡️',
  credits: '💳',
  email_templates: '📧',
  pages: '🌐',
  
};

export default function PluginDetailPage() {
  const { pluginKey } = useParams();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  
  const [plugin, setPlugin] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Data for different plugins
  const [bankAccounts, setBankAccounts] = useState([]);
  const [cryptoWallets, setCryptoWallets] = useState([]);
  const [paymentLinks, setPaymentLinks] = useState([]);
  const [credits, setCredits] = useState({ rules: [], history: [] });
  const [kycConfig, setKycConfig] = useState(null);
  const [kycStats, setKycStats] = useState(null);

  useEffect(() => {
    loadPlugin();
  }, [pluginKey]);

  const loadPlugin = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load plugin info
      const pluginData = await pluginsAPI.getAll();
      const allPlugins = [...(pluginData.purchased || []), ...(pluginData.available || [])];
      const currentPlugin = allPlugins.find(p => (p.pluginKey || p.plugin_key) === pluginKey);
      
      if (!currentPlugin) {
        setError('Plugin not found');
        return;
      }

      setPlugin(currentPlugin);

      // Load plugin-specific data based on plugin type
      if (pluginKey.startsWith('bank_transfer')) {
        const bankData = await paymentsAPI.getBankAccounts();
        setBankAccounts(bankData || []);
      } else if (pluginKey === 'crypto_payments') {
        const cryptoData = await paymentsAPI.getCryptoWallets();
        setCryptoWallets(cryptoData || []);
      } else if (pluginKey === 'payment_links') {
        const linksData = await paymentsAPI.getPaymentLinks();
        setPaymentLinks(linksData || []);
      } else if (pluginKey === 'credits') {
        const [rulesData, historyData] = await Promise.all([
          creditsAPI.getRules(),
          creditsAPI.getAllCredits()
        ]);
        setCredits({
          rules: rulesData?.rules || rulesData || [],
          history: historyData?.credits || historyData || []
        });
      } else if (pluginKey === 'kyc_verification') {
        const [configRes, statsRes] = await Promise.all([
          kycAPI.getConfig(),
          kycAPI.getStatistics()
        ]);
        setKycConfig(configRes.data?.data || configRes.data || configRes);
        setKycStats(statsRes.data?.data || statsRes.data || statsRes);
      }
      // Email templates plugin fetches its own data
      
    } catch (e) {
      setError(e.message || 'Failed to load plugin');
      console.error('Plugin load error:', e);
    } finally {
      setLoading(false);
    }
  };

  const renderPluginContent = () => {
    if (!isPluginRegistered(pluginKey)) {
      return (
        <div className="text-center py-12 sm:py-16">
          <Package className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 sm:mb-4" style={{ color: 'var(--text-tertiary)' }} />
          <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>
            No configuration panel available for this plugin.
          </p>
        </div>
      );
    }

    const config = getPluginConfig(pluginKey);
    const PanelComponent = config.component;

    // KYC Plugin
    if (pluginKey === 'kyc_verification') {
      return (
        <PanelComponent 
          config={kycConfig} 
          onUpdate={async (updates) => {
            try {
              await kycAPI.updateConfig(updates);
              await loadPlugin();
            } catch (e) {
              console.error('Failed to update KYC config:', e);
            }
          }} 
        />
      );
    }

    // Email Templates Plugin (self-contained)
    if (pluginKey === 'email_templates') {
      return <PanelComponent />;
    }

    if (pluginKey === 'pages') {
      return <PanelComponent />;
    }

    // Bank Transfer Plugins
    if (pluginKey.startsWith('bank_transfer')) {
      const handlers = {
        onSave: async (id, form, targets) => {
          try {
            const pluginTypeMap = { 
              bank_transfer_global: 'global', 
              bank_transfer_per_user: 'per_user', 
              bank_transfer_per_country: 'per_country' 
            };
            const formWithType = { ...form, pluginType: pluginTypeMap[pluginKey] || 'global' };
            
            let accountId = id;
            if (id) {
              await paymentsAPI.updateBankAccount(id, formWithType);
            } else {
              const res = await paymentsAPI.createBankAccount(formWithType);
              accountId = res?.id || res?.data?.id;
            }
            
            if (targets && accountId) {
              if (pluginKey === 'bank_transfer_per_user') {
                const existing = (bankAccounts.find(a => a.id === accountId)?.assignedUsers || []).map(u => u.user_id);
                for (const uid of targets) { 
                  if (!existing.includes(uid)) await paymentsAPI.assignBankToUser(accountId, { userId: uid }); 
                }
                for (const uid of existing) { 
                  if (!targets.includes(uid)) await paymentsAPI.removeUserAssignment(accountId, uid); 
                }
              }
              if (pluginKey === 'bank_transfer_per_country') {
                const existing = (bankAccounts.find(a => a.id === accountId)?.mappedCountries || []).map(c => c.country_code);
                for (const cc of targets) { 
                  if (!existing.includes(cc)) await paymentsAPI.mapBankToCountry(accountId, { countryCode: cc }); 
                }
                for (const cc of existing) { 
                  if (!targets.includes(cc)) await paymentsAPI.removeCountryMapping(accountId, cc); 
                }
              }
            }
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        },
        onDelete: async (id) => {
          try {
            await paymentsAPI.deleteBankAccount(id);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        },
        onRemoveUser: pluginKey === 'bank_transfer_per_user' ? async (bankId, userId) => {
          try {
            await paymentsAPI.removeUserAssignment(bankId, userId);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        } : undefined,
        onRemoveCountry: pluginKey === 'bank_transfer_per_country' ? async (bankId, cc) => {
          try {
            await paymentsAPI.removeCountryMapping(bankId, cc);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        } : undefined,
      };

      return <PanelComponent accounts={config.filterData(bankAccounts)} {...handlers} />;
    }

    // Crypto Payments
    if (pluginKey === 'crypto_payments') {
      const handlers = {
        onSave: async (id, form) => {
          try {
            if (id) await paymentsAPI.updateCryptoWallet(id, form);
            else await paymentsAPI.createCryptoWallet(form);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        },
        onDelete: async (id) => {
          try {
            await paymentsAPI.deleteCryptoWallet(id);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        }
      };
      return <PanelComponent wallets={cryptoWallets} {...handlers} />;
    }

    // Payment Links
    if (pluginKey === 'payment_links') {
      const handlers = {
        onSave: async (id, form) => {
          try {
            if (id) await paymentsAPI.updatePaymentLink(id, form);
            else await paymentsAPI.createPaymentLink(form);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        },
        onDelete: async (id) => {
          try {
            await paymentsAPI.deletePaymentLink(id);
            await loadPlugin();
          } catch (e) {
            setError(e.message);
          }
        }
      };
      return <PanelComponent links={paymentLinks} {...handlers} />;
    }

    // Credits Plugin
    if (pluginKey === 'credits') {
      return <PanelComponent credits={credits} onRefresh={loadPlugin} />;
    }

    return null;
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16">
        <Loader2 className="w-10 h-10 sm:w-12 sm:h-12 animate-spin mb-3 sm:mb-4" style={{ color: 'var(--accent-primary)' }} />
        <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>Loading plugin...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16">
        <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ color: 'var(--accent-danger)' }} />
        <p className="text-sm mb-3 sm:mb-4" style={{ color: 'var(--accent-danger)' }}>{error}</p>
        <button 
          onClick={() => navigate('/plugins')}
          className="flex items-center gap-2 text-xs sm:text-sm font-semibold px-4 py-2 rounded-lg transition-all"
          style={{ 
            backgroundColor: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
            color: 'var(--accent-primary)' 
          }}
        >
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Back to Plugins
        </button>
      </div>
    );
  }

  if (!plugin) {
    return (
      <div className="flex flex-col items-center justify-center py-12 sm:py-16">
        <Package className="w-10 h-10 sm:w-12 sm:h-12 mb-3 sm:mb-4" style={{ color: 'var(--text-tertiary)' }} />
        <p className="text-sm mb-3 sm:mb-4" style={{ color: 'var(--text-secondary)' }}>Plugin not found</p>
        <button 
          onClick={() => navigate('/plugins')}
          className="flex items-center gap-2 text-xs sm:text-sm font-semibold px-4 py-2 rounded-lg transition-all"
          style={{ 
            backgroundColor: 'color-mix(in srgb, var(--accent-primary) 10%, transparent)',
            color: 'var(--accent-primary)' 
          }}
        >
          <ArrowLeft className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
          Back to Plugins
        </button>
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // MOBILE LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  if (isMobile) {
    return (
      <div className="flex flex-col -mx-4 -my-6 min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
        
        {/* Hero Header */}
        <div className="relative overflow-hidden" style={{
          background: 'linear-gradient(135deg, var(--accent-primary) 0%, color-mix(in srgb, var(--accent-primary) 60%, var(--accent-info)) 100%)'
        }}>
          <div className="absolute -top-8 -right-8 w-32 h-32 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.06)' }} />
          <div className="absolute -bottom-12 -left-6 w-24 h-24 rounded-full" style={{ backgroundColor: 'rgba(255,255,255,0.04)' }} />

          <div className="relative px-4 pt-5 pb-5">
            {/* Back button */}
            <button
              onClick={() => navigate('/plugins')}
              className="flex items-center gap-2 text-xs font-semibold mb-4"
              style={{ color: 'rgba(255,255,255,0.9)' }}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Plugins
            </button>

            {/* Plugin icon + name */}
            <div className="flex items-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-xl flex items-center justify-center text-2xl"
                style={{ backgroundColor: 'rgba(255,255,255,0.15)' }}>
                {PLUGIN_ICONS[pluginKey] || '📦'}
              </div>
              <div className="flex-1 min-w-0">
                <h1 className="text-xl font-black text-white leading-tight">{plugin.label || plugin.name}</h1>
                {plugin.is_active && (
                  <div className="flex items-center gap-1 mt-1">
                    <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse" />
                    <span className="text-xs font-semibold" style={{ color: 'rgba(255,255,255,0.7)' }}>Active</span>
                  </div>
                )}
              </div>
            </div>

            {/* Description */}
            <p className="text-xs leading-relaxed" style={{ color: 'rgba(255,255,255,0.65)' }}>
              {plugin.description}
            </p>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pt-4 pb-8">
          {renderPluginContent()}
        </div>

        <div className="pb-[env(safe-area-inset-bottom)]" />
      </div>
    );
  }

  // ══════════════════════════════════════════════════════════════════════════
  // DESKTOP LAYOUT
  // ══════════════════════════════════════════════════════════════════════════
  return (
    <div className="space-y-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="space-y-4">
        <button
          onClick={() => navigate('/plugins')}
          className="flex items-center gap-2 text-sm font-semibold transition-all hover:gap-3"
          style={{ color: 'var(--accent-primary)' }}
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Plugins
        </button>

        <div className="flex items-center gap-4 sm:gap-6">
          <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-2xl flex items-center justify-center text-3xl sm:text-4xl flex-shrink-0"
            style={{ 
              backgroundColor: 'color-mix(in srgb, var(--accent-primary) 12%, transparent)',
              border: '1px solid color-mix(in srgb, var(--accent-primary) 20%, transparent)'
            }}
          >
            {PLUGIN_ICONS[pluginKey] || '📦'}
          </div>
          
          <div className="flex-1 min-w-0">
            <h1 className="text-xl sm:text-2xl font-bold mb-1" style={{ color: 'var(--text-primary)' }}>
              {plugin.label || plugin.name}
            </h1>
            <p className="text-xs sm:text-sm" style={{ color: 'var(--text-secondary)' }}>
              {plugin.description}
            </p>
          </div>

          {plugin.is_active && (
            <div className="px-3 sm:px-4 py-2 rounded-lg flex items-center gap-2 flex-shrink-0"
              style={{ 
                backgroundColor: 'color-mix(in srgb, var(--accent-success) 12%, transparent)',
                border: '1px solid color-mix(in srgb, var(--accent-success) 25%, transparent)'
              }}
            >
              <Settings className="w-3.5 h-3.5 sm:w-4 sm:h-4" style={{ color: 'var(--accent-success)' }} />
              <span className="text-xs sm:text-sm font-semibold" style={{ color: 'var(--accent-success)' }}>
                Active
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Plugin Content */}
      <div className="rounded-xl sm:rounded-2xl border p-4 sm:p-6"
        style={{ 
          backgroundColor: 'var(--bg-secondary)',
          borderColor: 'var(--border-color)'
        }}
      >
        {renderPluginContent()}
      </div>
    </div>
  );
}